import React from 'react';
import { Shield, Lock, Eye, Database, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
export function Privacy() {
  const {
    language
  } = useLanguage();
  const content = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: January 15, 2024',
      intro: 'At NEW ASIA TECH (PT NETKRIDA TUAH CAKRAWALA), we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.',
      sections: [{
        icon: Database,
        title: 'Information We Collect',
        content: 'We may collect information about you in a variety of ways. The information we may collect on the website includes: Personal Data (name, email address, company name), Usage Data (IP address, browser type, pages visited), and Cookies Data for analytics purposes.'
      }, {
        icon: Eye,
        title: 'How We Use Your Information',
        content: 'We use the information we collect to: Operate and maintain our website, improve user experience, analyze website usage and trends, send administrative information, respond to inquiries and provide customer support, and comply with legal obligations.'
      }, {
        icon: Lock,
        title: 'Data Security',
        content: 'We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure. We use industry-standard encryption and secure protocols to safeguard your data.'
      }, {
        icon: Shield,
        title: 'Your Rights',
        content: 'You have the right to: Access your personal data, request correction of inaccurate data, request deletion of your data, object to processing of your data, and withdraw consent at any time. To exercise these rights, please contact us at contact@newasiatech.co.id.'
      }],
      cookies: {
        title: 'Cookies Policy',
        content: 'We use cookies and similar tracking technologies to track activity on our website. Cookies are files with small amounts of data. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.'
      },
      contact: {
        title: 'Contact Us',
        content: 'If you have questions about this Privacy Policy, please contact us:',
        email: 'contact@newasiatech.co.id',
        address: 'PT NETKRIDA TUAH CAKRAWALA\nSavana Cluster, A2\nJl. Karya Bakti, Marpoyan Damai\nPekanbaru, Riau, Indonesia'
      }
    },
    id: {
      title: 'Kebijakan Privasi',
      lastUpdated: 'Terakhir Diperbarui: 15 Januari 2024',
      intro: 'Di NEW ASIA TECH (PT NETKRIDA TUAH CAKRAWALA), kami menganggap serius privasi Anda. Kebijakan Privasi ini menjelaskan bagaimana kami mengumpulkan, menggunakan, mengungkapkan, dan melindungi informasi Anda saat Anda mengunjungi website kami.',
      sections: [{
        icon: Database,
        title: 'Informasi yang Kami Kumpulkan',
        content: 'Kami dapat mengumpulkan informasi tentang Anda dengan berbagai cara. Informasi yang mungkin kami kumpulkan di website meliputi: Data Pribadi (nama, alamat email, nama perusahaan), Data Penggunaan (alamat IP, jenis browser, halaman yang dikunjungi), dan Data Cookie untuk tujuan analitik.'
      }, {
        icon: Eye,
        title: 'Bagaimana Kami Menggunakan Informasi Anda',
        content: 'Kami menggunakan informasi yang kami kumpulkan untuk: Mengoperasikan dan memelihara website kami, meningkatkan pengalaman pengguna, menganalisis penggunaan dan tren website, mengirim informasi administratif, menanggapi pertanyaan dan memberikan dukungan pelanggan, serta mematuhi kewajiban hukum.'
      }, {
        icon: Lock,
        title: 'Keamanan Data',
        content: 'Kami menerapkan langkah-langkah keamanan teknis dan organisasi yang sesuai untuk melindungi informasi pribadi Anda. Namun, tidak ada metode transmisi melalui Internet yang 100% aman. Kami menggunakan enkripsi standar industri dan protokol aman untuk melindungi data Anda.'
      }, {
        icon: Shield,
        title: 'Hak Anda',
        content: 'Anda memiliki hak untuk: Mengakses data pribadi Anda, meminta koreksi data yang tidak akurat, meminta penghapusan data Anda, menolak pemrosesan data Anda, dan menarik persetujuan kapan saja. Untuk menggunakan hak-hak ini, silakan hubungi kami di contact@newasiatech.co.id.'
      }],
      cookies: {
        title: 'Kebijakan Cookie',
        content: 'Kami menggunakan cookie dan teknologi pelacakan serupa untuk melacak aktivitas di website kami. Cookie adalah file dengan sejumlah kecil data. Anda dapat menginstruksikan browser Anda untuk menolak semua cookie atau untuk menunjukkan kapan cookie sedang dikirim.'
      },
      contact: {
        title: 'Hubungi Kami',
        content: 'Jika Anda memiliki pertanyaan tentang Kebijakan Privasi ini, silakan hubungi kami:',
        email: 'contact@newasiatech.co.id',
        address: 'PT NETKRIDA TUAH CAKRAWALA\nSavana Cluster, A2\nJl. Karya Bakti, Marpoyan Damai\nPekanbaru, Riau, Indonesia'
      }
    }
  };
  const text = content[language];
  return <>
      <SEO title={`${text.title} | NEW ASIA TECH`} description={text.intro} url="/privacy" type="article" data-id="element-413" />

      <div className="min-h-screen pt-32 pb-20 px-6" data-id="element-414">
        <div className="max-w-4xl mx-auto" data-id="element-415">
          <header className="mb-16 animate-fade-in-up" data-id="element-416">
            <h1 className="text-4xl md:text-6xl font-display font-bold text-ivory mb-4" data-id="element-417">
              {text.title}
            </h1>
            <p className="text-sm font-mono text-muted-gold" data-id="element-418">
              {text.lastUpdated}
            </p>
          </header>

          <div className="prose prose-invert max-w-none" data-id="element-419">
            <div className="bg-white/[0.02] border border-white/10 p-8 rounded-lg mb-12 animate-fade-in-up delay-100" data-id="element-420">
              <p className="text-gray-400 leading-relaxed" data-id="element-421">{text.intro}</p>
            </div>

            <div className="space-y-12" data-id="element-422">
              {text.sections.map((section, idx) => {
              const Icon = section.icon;
              return <section key={idx} className={`animate-fade-in-up delay-${(idx + 2) * 100}`} data-id="element-423">
                    <div className="flex items-center mb-6" data-id="element-424">
                      <div className="p-3 bg-white/5 border border-white/10 rounded mr-4" data-id="element-425">
                        <Icon className="w-6 h-6 text-ai-teal" data-id="element-426" />
                      </div>
                      <h2 className="text-2xl font-display font-bold text-ivory" data-id="element-427">
                        {section.title}
                      </h2>
                    </div>
                    <p className="text-gray-400 leading-relaxed pl-16" data-id="element-428">
                      {section.content}
                    </p>
                  </section>;
            })}
            </div>

            <div className="mt-16 bg-neural-green/5 border border-neural-green/20 p-8 rounded-lg animate-fade-in-up delay-600" data-id="element-429">
              <h2 className="text-2xl font-display font-bold text-ivory mb-4 flex items-center" data-id="element-430">
                <Shield className="w-6 h-6 text-neural-green mr-3" data-id="element-431" />
                {text.cookies.title}
              </h2>
              <p className="text-gray-400 leading-relaxed" data-id="element-432">
                {text.cookies.content}
              </p>
            </div>

            <div className="mt-16 border-t border-white/10 pt-12 animate-fade-in-up delay-700" data-id="element-433">
              <h2 className="text-2xl font-display font-bold text-ivory mb-6 flex items-center" data-id="element-434">
                <Mail className="w-6 h-6 text-muted-gold mr-3" data-id="element-435" />
                {text.contact.title}
              </h2>
              <p className="text-gray-400 mb-6" data-id="element-436">{text.contact.content}</p>
              <div className="bg-white/[0.02] border border-white/10 p-6 rounded-lg" data-id="element-437">
                <div className="mb-4" data-id="element-438">
                  <div className="text-xs font-mono text-muted-gold mb-2" data-id="element-439">
                    EMAIL
                  </div>
                  <a href={`mailto:${text.contact.email}`} className="text-ivory hover:text-ai-teal transition-colors" data-id="element-440">
                    {text.contact.email}
                  </a>
                </div>
                <div data-id="element-441">
                  <div className="text-xs font-mono text-muted-gold mb-2" data-id="element-442">
                    ADDRESS
                  </div>
                  <address className="text-gray-400 not-italic whitespace-pre-line" data-id="element-443">
                    {text.contact.address}
                  </address>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}